/**
 * HamburgerMenu – Drawer-Navigation mit allen Bereichen.
 * Öffnet auf Klick auf den Hamburger-Button im Header.
 * Seit NT-LAYOUT-01 einziger Mobile-Zugang zu Business, Immobilien, Community, Organisation,
 * Profil, Kladde, Brett und Optionen (die Bottom-Nav zeigt nur noch die fünf Spiel-Bereiche).
 */

import { navigate, onRouteChange } from '../router.js';

interface SubItem {
  label: string;
  path:  string | null;
  badge?: string;
}

interface Section {
  id:    string;
  label: string;
  paths: string[];
  items: SubItem[];
}

const SECTIONS: Section[] = [
  {
    id: 'karriere',
    label: 'KARRIERE',
    paths: ['/jobs', '/talentbaum', '/dashboard', '/profile'],
    items: [
      { label: 'JOBS',       path: '/jobs' },
      { label: 'TALENTBAUM', path: '/talentbaum' },
      { label: 'PROFIL',     path: '/profile' },
      { label: 'ITEMS',      path: null, badge: 'BALD' },
    ],
  },
  {
    id: 'business',
    label: 'BUSINESS',
    paths: ['/business/crime-ops', '/business/lagerhaus', '/business/markt', '/business/boerse'],
    items: [
      { label: 'MARKT',     path: '/business/markt' },
      { label: 'BÖRSE',     path: '/business/boerse' },
      { label: 'LAGERHAUS', path: '/business/lagerhaus' },
      { label: 'CRIME OPS', path: '/business/crime-ops' },
    ],
  },
  {
    id: 'immobilien',
    label: 'IMMOBILIEN',
    paths: ['/immobilien/geschaefte', '/immobilien/lager', '/immobilien/verstecke'],
    items: [
      { label: 'GESCHÄFTE', path: '/immobilien/geschaefte' },
      { label: 'LAGER',     path: '/immobilien/lager' },
      { label: 'VERSTECKE', path: '/immobilien/verstecke' },
    ],
  },
  {
    id: 'community',
    label: 'COMMUNITY',
    paths: ['/community/leaderboard', '/community/stream', '/community/marktplatz'],
    items: [
      // NT-LAYOUT-01: war hier fälschlich noch „BALD", in der Desktop-Seitenleiste längst verfügbar
      { label: 'STREAM',      path: '/community/stream' },
      { label: 'MARKTPLATZ',  path: null, badge: 'BALD' },
      { label: 'LEADERBOARD', path: '/community/leaderboard' },
    ],
  },
  {
    // NT-ORG-01: S2 — eigene Nav-Gruppe, bewusst nicht unter COMMUNITY oder ACCOUNT verschachtelt.
    id: 'organisation',
    label: 'ORGANISATION',
    paths: ['/organisation'],
    items: [
      { label: 'ORGANISATION', path: '/organisation' },
    ],
  },
];

let drawerEl:   HTMLElement | null = null;
let backdropEl: HTMLElement | null = null;
let isOpen = false;

function createDrawerStyles(): void {
  if (document.getElementById('hamburger-styles')) return;
  const s = document.createElement('style');
  s.id = 'hamburger-styles';
  s.textContent = `
    .dash-hamburger {
      font-family: var(--font-mono); font-size: 18px; background: transparent;
      border: 1px solid transparent; color: var(--text-muted); cursor: pointer;
      padding: 4px 8px; border-radius: var(--radius-btn); line-height: 1;
      touch-action: manipulation; min-height: 44px; min-width: 44px;
      display: flex; align-items: center; justify-content: center;
    }
    .dash-hamburger:hover { color: var(--text-primary); border-color: var(--border); }

    .dash-drawer-backdrop {
      position: fixed; inset: 0; background: rgba(0,0,0,.55);
      z-index: 299; display: none; touch-action: none;
    }
    .dash-drawer-backdrop.is-open { display: block; }

    /* NT-LAYOUT-01 (L3): Das gesamte Panel ist EIN Scroll-Container — vorher scrollte nur die
       Navigationsliste, ACCOUNT und der Footer standen fest darunter und waren auf kleinen
       Bildschirmen nicht erreichbar. Einzige Ausnahme vom Mitscrollen: der Schließen-Button. */
    .dash-drawer {
      position: fixed; top: 0; right: 0; bottom: 0; width: 240px; height: 100dvh;
      background: var(--bg-surface); border-left: 1px solid var(--border);
      z-index: 300; display: flex; flex-direction: column;
      overflow-y: auto; overscroll-behavior: contain;
      padding-top: env(safe-area-inset-top);
      padding-bottom: env(safe-area-inset-bottom);
      transform: translateX(100%); transition: transform 200ms ease;
    }
    .dash-drawer > * { flex-shrink: 0; }
    .dash-drawer.is-open { transform: translateX(0); }

    .dash-drawer-header {
      display: flex; align-items: center;
      padding: var(--space-3) var(--space-4);
      padding-right: 52px; /* Platz für den Schließen-Button, der in derselben Zeile schwebt */
      border-bottom: 1px solid var(--border); min-height: 52px;
    }
    .dash-drawer-section-label {
      font-family: var(--font-mono); font-size: var(--font-size-xs);
      color: var(--accent-gold); letter-spacing: .12em;
    }
    /* Bleibt beim Scrollen oben rechts stehen (sticky im Drawer-Scroll-Container). Die negative
       Unterkante zieht die Kopfzeile in dieselbe Zeile hoch. */
    .dash-drawer-close {
      position: sticky; top: 0; z-index: 1; align-self: flex-end;
      margin-bottom: -52px;
      font-family: var(--font-mono); font-size: 16px; background: var(--bg-surface);
      border: none; color: var(--text-muted); cursor: pointer;
      width: 52px; height: 52px; padding: 0;
      display: flex; align-items: center; justify-content: center;
    }
    .dash-drawer-close:hover { color: var(--text-primary); }

    .dash-drawer-nav { padding: var(--space-1) 0; }
    .dash-drawer-group + .dash-drawer-group { border-top: 1px solid var(--border); }
    .dash-drawer-group-label {
      font-family: var(--font-mono); font-size: var(--font-size-xs);
      color: var(--text-muted); letter-spacing: .12em;
      padding: var(--space-3) var(--space-4) var(--space-1); margin: 0;
    }
    .dash-drawer-item {
      display: block; width: 100%; text-align: left;
      font-family: var(--font-mono); font-size: var(--font-size-sm);
      letter-spacing: .06em; text-transform: uppercase;
      padding: var(--space-3) var(--space-4);
      background: transparent; border: none; border-left: 2px solid transparent;
      color: var(--text-primary); cursor: pointer; text-decoration: none;
    }
    .dash-drawer-item.is-active { color: var(--accent-gold); border-left-color: var(--accent-gold); }
    .dash-drawer-item.is-locked { color: var(--text-muted); cursor: default; pointer-events: none; }
    .dash-drawer-item:hover:not(.is-locked) { background: var(--bg-elevated); }
    .dash-drawer-badge {
      font-size: 9px; letter-spacing: .06em; margin-left: 6px;
      color: var(--text-muted); border: 1px solid var(--border);
      padding: 1px 4px; border-radius: 2px; vertical-align: middle;
    }
    .dash-drawer-footer {
      padding: var(--space-3) var(--space-4);
      border-top: 1px solid var(--border);
    }
    .dash-drawer-footer-link {
      display: flex; align-items: center; min-height: var(--touch-target-min);
      font-family: var(--font-mono); font-size: var(--font-size-xs);
      color: var(--text-muted); letter-spacing: .08em; text-decoration: none;
    }
    .dash-drawer-footer-link:hover { color: var(--text-primary); }
  `;
  document.head.appendChild(s);
}

function buildDrawer(): void {
  if (document.getElementById('dash-drawer-backdrop')) return;

  backdropEl = document.createElement('div');
  backdropEl.className = 'dash-drawer-backdrop';
  backdropEl.id = 'dash-drawer-backdrop';
  backdropEl.addEventListener('click', closeDrawer);
  document.body.appendChild(backdropEl);

  drawerEl = document.createElement('div');
  drawerEl.className = 'dash-drawer';
  drawerEl.id = 'dash-drawer';
  document.body.appendChild(drawerEl);

  updateDrawerContent(window.location.pathname);
}

function updateDrawerContent(path: string): void {
  if (!drawerEl) return;

  const groups = SECTIONS.map((section) => {
    const items = section.items.map((item) => {
      const isActive = item.path !== null && item.path === path;
      const isLocked = !item.path;
      const badge    = item.badge ? `<span class="dash-drawer-badge">${item.badge}</span>` : '';
      const cls      = `dash-drawer-item${isActive ? ' is-active' : ''}${isLocked ? ' is-locked' : ''}`;
      if (item.path) {
        return `<a class="${cls}" href="${item.path}" data-drawer-nav="${item.path}">${item.label}${badge}</a>`;
      }
      return `<span class="${cls}">${item.label}${badge}</span>`;
    }).join('');
    return `
      <div class="dash-drawer-group">
        <p class="dash-drawer-group-label">${section.label}</p>
        ${items}
      </div>
    `;
  }).join('');

  drawerEl.innerHTML = `
    <button class="dash-drawer-close" id="dash-drawer-close" aria-label="Menü schließen" type="button">&#10005;</button>
    <div class="dash-drawer-header">
      <span class="dash-drawer-section-label">NAVIGATION</span>
    </div>
    <nav class="dash-drawer-nav">
      ${groups}
    </nav>
    <div class="dash-drawer-group dash-drawer-group--account">
      <p class="dash-drawer-group-label">ACCOUNT</p>
      <a class="dash-drawer-item${path === '/kladde' ? ' is-active' : ''}" href="/kladde" data-drawer-nav="/kladde">KLADDE</a>
      <a class="dash-drawer-item${path === '/brett' ? ' is-active' : ''}" href="/brett" data-drawer-nav="/brett">BRETT</a>
      <a class="dash-drawer-item${path === '/settings' ? ' is-active' : ''}" href="/settings" data-drawer-nav="/settings">OPTIONEN</a>
    </div>
    <footer class="dash-drawer-footer">
      <a href="/impressum" class="dash-drawer-footer-link">IMPRESSUM</a>
      <a href="/datenschutz" class="dash-drawer-footer-link">DATENSCHUTZ</a>
    </footer>
  `;

  drawerEl.querySelector('#dash-drawer-close')?.addEventListener('click', closeDrawer);
  drawerEl.querySelectorAll<HTMLElement>('[data-drawer-nav]').forEach((el) => {
    el.addEventListener('click', (e) => {
      e.preventDefault();
      const target = (el as HTMLAnchorElement).dataset.drawerNav ?? '';
      closeDrawer();
      navigate(target);
    });
  });
}

function openDrawer(): void {
  if (!drawerEl || !backdropEl) return;
  updateDrawerContent(window.location.pathname);
  drawerEl.classList.add('is-open');
  backdropEl.classList.add('is-open');
  isOpen = true;
}

function closeDrawer(): void {
  if (!drawerEl || !backdropEl) return;
  drawerEl.classList.remove('is-open');
  backdropEl.classList.remove('is-open');
  isOpen = false;
}

export function initHamburgerMenu(): void {
  createDrawerStyles();
  buildDrawer();
  onRouteChange((path: string) => {
    closeDrawer();
    updateDrawerContent(path);
  });
}

export function toggleHamburgerMenu(): void {
  if (isOpen) { closeDrawer(); } else { openDrawer(); }
}

export const HAMBURGER_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>`;
